import React, { useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DashboardIcon from '@material-ui/icons/Dashboard';
import ServerIcon from '@material-ui/icons/Storage';
import GamepadIcon from '@material-ui/icons/SportsEsports';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import EmailIcon from '@material-ui/icons/Email';
import PublicIcon from '@material-ui/icons/Public';

import StatsCard from './shared/StatsCard';
import LiveIndicator from './shared/LiveIndicator';
import RefreshButton from './shared/RefreshButton';
import AgentList from './ServerMonitoring/AgentList';
import SteamTokenSettings from './ServerMonitoring/SteamTokenSettings';
import DiscordInviteSettings from './ServerMonitoring/DiscordInviteSettings';
import MailProviderSettings from './ServerMonitoring/MailProviderSettings';
import QueueSettings from './ServerMonitoring/QueueSettings';
import WingmanQueueSettings from './ServerMonitoring/WingmanQueueSettings';
import FounderBadgeSettings from './ServerMonitoring/FounderBadgeSettings';
import QueueScheduleSettings from './ServerMonitoring/QueueScheduleSettings';
import MatchmakingAlgorithmSettings from './ServerMonitoring/MatchmakingAlgorithmSettings';
import MapVoteSettings from './ServerMonitoring/MapVoteSettings';
import GeoQueueSettings from './ServerMonitoring/GeoQueueSettings';
import CapacityPlannerSettings from './ServerMonitoring/CapacityPlannerSettings';
import ActiveMatches from './MatchMonitoring/ActiveMatches';
import SystemHealth from './SystemHealth/HealthOverview';
import AlertsPanel from './Alerts/AlertsPanel';
import RegionManagement from './InfrastructureManagement/RegionManagement';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  header: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  titleIcon: {
    fontSize: 40,
    color: theme.palette.primary.main
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  section: {
    marginTop: theme.spacing(4)
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  },
  errorContainer: {
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  analyticsCard: {
    padding: theme.spacing(2),
    height: '100%'
  },
  eventRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  eventLabel: {
    color: theme.palette.text.secondary
  },
  eventValue: {
    fontWeight: 700
  },
  recentList: {
    maxHeight: 280,
    overflowY: 'auto'
  }
}));

const ONBOARDING_EVENT_LABELS = {
  steam_signup_completed_no_email: 'Steam signups without email',
  steam_signup_completed_no_country: 'Steam signups without country',
  geoip_detected_country_applied: 'GeoIP country applied',
  tier1_user_returned_within_7d: 'Tier 1 returned within 7d',
  tier1_user_returned_within_30d: 'Tier 1 returned within 30d',
  admin_approved_without_email: 'Admin approvals without email',
  admin_approved_without_user_country: 'Admin approvals without user country',
  email_added_after_signup: 'Email added after signup'
};

const ONBOARDING_EVENT_ORDER = [
  'steam_signup_completed_no_email',
  'steam_signup_completed_no_country',
  'geoip_detected_country_applied',
  'admin_approved_without_email',
  'admin_approved_without_user_country',
  'email_added_after_signup',
  'tier1_user_returned_within_7d',
  'tier1_user_returned_within_30d'
];

function formatEventLabel(event) {
  return ONBOARDING_EVENT_LABELS[event] || String(event || '').replace(/_/g, ' ');
}

function formatEventTime(value) {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString();
}

const DashboardOverview = ({ 
  dashboard, 
  loading, 
  error, 
  onRefresh,
  realtimeConnected = false,
  userId
}) => {
  const classes = useStyles();
  
  if (loading && !dashboard.overview.timestamp) {
    return (
      <Container className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container className={classes.root}>
        <Paper className={classes.errorContainer}>
          <ErrorIcon color="error" style={{ fontSize: 60, marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>
            Error Loading Dashboard
          </Typography>
          <Typography color="textSecondary">
            {error}
          </Typography>
          <Box mt={2}>
            <RefreshButton onRefresh={onRefresh} />
          </Box>
        </Paper>
      </Container>
    );
  }
  
  const { agents, matches, system, onboarding = {} } = dashboard.overview;
  const agentList = Array.isArray(agents?.agents) ? agents.agents : [];
  const onboardingTotals = onboarding?.totalByEvent || {};
  const onboardingLast24h = onboarding?.last24hByEvent || {};
  const recentOnboardingEvents = Array.isArray(onboarding?.recentEvents)
    ? onboarding.recentEvents
    : [];

  // Derive counters from list when server-side aggregates are missing or inconsistent
  const derived = agentList.reduce(
    (acc, a) => {
      acc.total++;
      if (a?.state === 'IDLE') acc.idle++;
      else acc.busy++;

      if (a?.health === 'healthy') acc.healthy++;
      else if (a?.health === 'degraded') acc.degraded++;
      else if (a?.health === 'unhealthy') acc.unhealthy++;
      else acc.degraded++; // unknown → degraded bucket (keeps "not healthy" visible)
      return acc;
    },
    { total: 0, healthy: 0, degraded: 0, unhealthy: 0, idle: 0, busy: 0 }
  );

  const totalServers = Number(agents?.total ?? 0) || derived.total;
  const healthyServers = Number(agents?.healthy ?? 0) || derived.healthy;
  const degradedServers = Number(agents?.degraded ?? 0) || derived.degraded;
  const unhealthyServers = Number(agents?.unhealthy ?? 0) || derived.unhealthy;
  const idleServers = Number(agents?.idle ?? 0) || derived.idle;
  const busyServers = Number(agents?.busy ?? 0) || derived.busy;

  const avgCpu = Number(agents?.avgCpuUsage ?? 0);
  const avgMem = Number(agents?.avgMemoryUsage ?? 0);
  
  return (
    <Container className={classes.root} maxWidth="xl">
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.title}>
          <DashboardIcon className={classes.titleIcon} />
          <Typography variant="h4" component="h1">
            System Dashboard
          </Typography>
        </Box>
        
        <Box className={classes.headerActions}>
          <LiveIndicator live={realtimeConnected} text="Real-time" />
          <RefreshButton onRefresh={onRefresh} loading={loading} />
        </Box>
      </Box>
      
      {/* Agent Stats */}
      <Typography variant="h6" className={classes.sectionTitle}>
        <ServerIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
        Game Servers
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Servers"
            value={totalServers}
            subtitle="Registered agents"
            icon={<ServerIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Healthy"
            value={healthyServers}
            subtitle={`${degradedServers} degraded, ${unhealthyServers} unhealthy`}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Idle"
            value={idleServers}
            subtitle={`${busyServers} busy`}
            icon={<TrendingUpIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Avg CPU"
            value={`${avgCpu.toFixed(1)}%`}
            subtitle={`Memory: ${avgMem.toFixed(1)}%`}
            icon={<DashboardIcon />}
            color={avgCpu > 80 ? 'error' : 'primary'}
          />
        </Grid>
      </Grid>

      {/* Onboarding Analytics */}
      <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: 32 }}>
        <PersonAddIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
        Tier 1 Onboarding Funnel
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Funnel Events"
            value={onboarding.totalEvents || 0}
            subtitle={`Last ${onboarding.windowDays || 30} days`}
            icon={<PersonAddIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="No Email"
            value={onboardingTotals.steam_signup_completed_no_email || 0}
            subtitle={`${onboardingLast24h.steam_signup_completed_no_email || 0} in last 24h`}
            icon={<EmailIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="GeoIP Applied"
            value={onboardingTotals.geoip_detected_country_applied || 0}
            subtitle={`${onboardingLast24h.geoip_detected_country_applied || 0} in last 24h`}
            icon={<PublicIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Email Added Later"
            value={onboardingTotals.email_added_after_signup || 0}
            subtitle={`${onboardingLast24h.email_added_after_signup || 0} in last 24h`}
            icon={<EmailIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.analyticsCard}>
            <Typography variant="subtitle1" gutterBottom>
              Event Counts
            </Typography>
            {ONBOARDING_EVENT_ORDER.map((event) => (
              <Box key={event} className={classes.eventRow}>
                <Typography variant="body2" className={classes.eventLabel}>
                  {formatEventLabel(event)}
                </Typography>
                <Typography variant="body2" className={classes.eventValue}>
                  {onboardingTotals[event] || 0}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.analyticsCard}>
            <Typography variant="subtitle1" gutterBottom>
              Recent Funnel Events
            </Typography>
            {recentOnboardingEvents.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No onboarding funnel events recorded in the selected window.
              </Typography>
            ) : (
              <List dense className={classes.recentList}>
                {recentOnboardingEvents.map((event, index) => (
                  <React.Fragment key={`${event._id || event.event}-${event.createdAt || index}`}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem>
                      <ListItemText
                        primary={formatEventLabel(event.event)}
                        secondary={[
                          formatEventTime(event.createdAt),
                          event.userId ? `user: ${event.userId}` : null,
                          event.region ? `country: ${event.region}` : null
                        ].filter(Boolean).join(' • ')}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Box mt={3}>
        <SteamTokenSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <DiscordInviteSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <MailProviderSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <QueueSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <WingmanQueueSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <FounderBadgeSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <QueueScheduleSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <MatchmakingAlgorithmSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <MapVoteSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <GeoQueueSettings userId={userId} />
      </Box>

      <Box mt={3}>
        <CapacityPlannerSettings userId={userId} />
      </Box>

      {/* Match Stats */}
      <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: 32 }}>
        <GamepadIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
        Matches
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Now"
            value={matches.active}
            subtitle="Currently playing"
            icon={<GamepadIcon />}
            color="success"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Created Today"
            value={matches.createdToday}
            subtitle={`${matches.createdLastHour} in last hour`}
            icon={<TrendingUpIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Completed"
            value={matches.completedToday}
            subtitle="Today"
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Completion Rate"
            value={matches.completionRate}
            subtitle={`${matches.cancelledToday} cancelled`}
            icon={<WarningIcon />}
            color={parseFloat(matches.completionRate) > 80 ? 'success' : 'warning'}
          />
        </Grid>
      </Grid>
      
      {/* Agent List */}
      <Box className={classes.section}>
        <AgentList agents={agentList} />
      </Box>
      
      {/* Active Matches */}
      {matches.active > 0 && (
        <Box className={classes.section}>
          <ActiveMatches />
        </Box>
      )}
      
      {/* System Health */}
      <Box className={classes.section}>
        <SystemHealth system={system} />
      </Box>
      
      {/* Alerts Panel */}
      <Box className={classes.section}>
        <AlertsPanel alerts={dashboard.overview.alerts || []} />
      </Box>

      {/* Infrastructure — Server Regions */}
      <Box className={classes.section}>
        <RegionManagement userId={userId} />
      </Box>
    </Container>
  );
};

export default DashboardOverview;

